import { inject, Injectable } from '@angular/core';
import { Unsubscribe, User } from '@angular/fire/auth';
import {
  DatabaseReference,
  child,
  get,
  getDatabase,
  ref,
  remove,
  update,
  onValue,
  push,
  set,
} from '@angular/fire/database';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { Analytics, getAnalytics, setUserId } from '@angular/fire/analytics';

import { environment } from '../../../environments/current';
import { Preference, PreferenceObject, ProviderEnum } from '../models/preference';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class PreferenceService {
  private authService = inject(AuthService);
  private logger = inject(LoggerService);
  private storage = inject(LocalStorageService);
  private analytics = inject(Analytics);

  private static firstGame = '3';
  public static nameMin = 3;
  public static nameMax = 30;
  private userRef: DatabaseReference;
  private prefSub: Unsubscribe;
  private _items: BehaviorSubject<Preference> = new BehaviorSubject(undefined);
  public readonly items: Observable<Preference> = this._items.asObservable();
  private _userId: BehaviorSubject<string> = new BehaviorSubject(undefined);
  public readonly userId: Observable<string> = this._userId.asObservable();
  public readonly userIdValue: BehaviorSubject<string> = this._userId;
  private _hasFamilyRules: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly hasFamilyRules: Observable<boolean> = this._hasFamilyRules.asObservable();
  public selectedMatch: BehaviorSubject<string> = new BehaviorSubject(PreferenceService.firstGame);

  constructor() {
    this.init();
    this.selectedMatch.next(this.storage.get('matchId'));
  }

  private forcePreferenceCreate(user: User, userRef: DatabaseReference): Promise<void> {
    this.logger.warning('Pref:Init:forcePreferenceCreate');
    const newPrefs = new PreferenceObject();
    newPrefs.email = user.email;
    if (user.displayName != null) {
      newPrefs.name = user.displayName;
    }
    return update(userRef, newPrefs)
      .then(() => {
        this.logger.debug('Pref:Init:SetOk');
      })
      .catch(err => {
        this.logger.error({ 'Pref:Init:Set': err });
      }); // ao inserir novos valores o subscribe executa novamente
  }

  private init(): void {
    this.authService.user.subscribe(user => {
      this._hasFamilyRules.next(false);
      if (user) {
        this.logger.debug({ 'Pref:Init:User': user });
        this._userId.next(user.uid);
        this.userRef = ref(getDatabase(), `usuarios/${user.uid}`);
        this.prefSub = onValue(this.userRef, snaphot => {
          this.logger.debug(snaphot);
          this.logger.debug('Pref:Init:SubOk');
          const providers = user.providerData.map(element => {
            return element.providerId;
          });

          if (!snaphot) {
            this.forcePreferenceCreate(user, this.userRef);
          }
          const values = { userId: snaphot.key, ...snaphot.val() };
          if (values.provider === ProviderEnum.Password || values.provider === ProviderEnum.Email) {
            values.providerPassword = true;
          } else if (values.provider === ProviderEnum.Google || providers.includes(ProviderEnum.Google)) {
            values.providerGoogle = true;
          } else if (
            values.provider === ProviderEnum.Facebook ||
            values.provider === 'facebook' ||
            providers.includes(ProviderEnum.Facebook)
          ) {
            values.providerFacebook = true;
          }
          if (user.photoURL && (!values.image || user.photoURL !== values.image)) {
            update(this.userRef, { image: user.photoURL })
              .then(() => {
                this.logger.debug('Imagem do usuário atualizada');
              })
              .catch(err => {
                this.logger.error({ 'Prefs:Init:Image': err });
              });
          }
          if (values.grupos) {
            Object.keys(values.grupos).forEach(groupId => {
              if (environment.familyRules.includes(groupId)) {
                this._hasFamilyRules.next(true);
              }
            });
          }
          if (values.notifyToken) {
            values.notifyTokens = Object.keys(values.notifyToken);
            this.logger.debug({ 'Prefs:Init:NotifyTokens': values.notifyTokens });
          }
          this._items.next(values);
        });
        setUserId(this.analytics, user.uid);
      } else {
        this.logger.debug('Prefs:Init:NoUser');
        this.userRef = undefined;
        this._items.next(undefined);
        this._userId.next(undefined);
      }
    });
  }

  public update(newValues: Preference): Promise<void> {
    this.logger.debug({ 'Pref:Update': newValues });
    if (this.userRef) {
      return update(this.userRef, newValues)
        .catch(err => {
          this.logger.debug({ 'Pref:Update': err });
        })
        .catch(err => {
          this.logger.error({ 'Pref:Update:Set': err });
        });
    } else {
      this.logger.error('Pref:Update:NoUserRef');
    }
  }

  public delete(prefField: string): Promise<void> {
    this.logger.debug({ 'Pref:Delete': prefField });

    if (prefField != '') {
      return remove(child(this.userRef, prefField))
        .catch(err => {
          this.logger.debug({ 'Pref:Delete': err });
        })
        .catch(err => {
          this.logger.error({ 'Pref:Delete:Set': err });
        });
    }
  }

  public setSelectedGroup(groupId: string | undefined): Promise<void> {
    if (groupId === undefined) {
      return this.delete('grupoSelecionado');
    } else {
      return this.update({ grupoSelecionado: groupId });
    }
  }

  public setSelectedMatch(matchId: string): boolean {
    this.logger.debug({ 'Pref:setMatch': matchId });
    this.selectedMatch.next(matchId);

    return this.storage.set('matchId', matchId);
  }
  /*
  public selectedGroup(): Observable<string> {

    return this.items.pipe(
      map( (items) => (items) ? items.grupoSelecionado ?? PreferenceService.firstGame : PreferenceService.firstGame), 
      distinctUntilChanged(),
      share()
    );
  }
*/
  public acceptInvite(groupId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const conviteRef = child(this.userRef, `convites/${groupId}`);
      const gruposRef = child(this.userRef, 'grupos');
      const promises: Promise<void>[] = [];
      promises.push(
        get(conviteRef)
          .then(snapshot => {
            this.logger.debug({ 'Pref:acceptInvite': snapshot.val() });

            return update(gruposRef, { [groupId]: snapshot.val() }).catch(err => {
              this.logger.error({ 'Pref:AcceptInvite:Update': err });
            });
          })
          .catch(err => {
            this.logger.error({ 'Pref:AcceptInvite:Get': err });
          })
      );
      promises.push(
        remove(child(this.userRef, `convites/${groupId}`)).catch(err => {
          this.logger.error({ 'Pref:AcceptInvite:Get': err });
        })
      );

      return Promise.all(promises)
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Obtem a URL da imagem do usuário no banco
   * @returns a URL da imagem
   */
  public get image(): Observable<string | undefined> {
    return this.items.pipe(
      map(prefs => {
        if (prefs && prefs.image) {
          let imagem = prefs.image;
          if (imagem && imagem.startsWith('https://graph.facebook.com/')) {
            imagem += '?type=large';
          }
          return imagem;
        } else {
          return undefined;
        }
      })
    );
  }

  /**
   * Atualiza token do notify
   * @returns
   */
  public setNotifyToken(token: string | null, updateAvisos: boolean = false): Promise<void[]> {
    const promises: Promise<void>[] = [];
    if (token === null || updateAvisos) {
      const updateValues = {
        avisos: !(token === null),
      };
      promises.push(
        update(this.userRef, updateValues).catch(err => {
          this.logger.error({ 'Pref:setNotifyToken:Update': err });
        })
      );
    } else if (token !== null) {
      const pushRef = child(this.userRef, 'notifyToken');
      promises.push(update(pushRef, { [token]: Date.now() }));
    }
    return Promise.all(promises);
  }
}
